import { m } from 'framer-motion'
import { Bell } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsAuthenticated } from '@/features/auth/store/authStore'
import { badgePopVariants } from '@/shared/motion'

import { useUnreadCountQuery } from '../hooks/useNotifications'

import { NotificationDropdown } from './NotificationDropdown'

/**
 * Bell brutal del header. Botón con icono + badge `.chip` rojo si
 * `count > 0`. Click → toggle dropdown. Esc + click fuera cierran.
 *
 * <p>No renderiza nada si el usuario no está autenticado — anónimos no
 * tienen notificaciones. El `useUnreadCountQuery` también respeta
 * `isAuthenticated` para no disparar el polling sin sesión.</p>
 */
export function NotificationBell() {
  const { t } = useTranslation()
  const isAuthenticated = useIsAuthenticated()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { data } = useUnreadCountQuery()

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  if (!isAuthenticated) return null

  // El número rojo cuenta solo «Tus partidas» (spec 2026-11-03-campana-pestanas); las novedades
  // sin leer, un punto. Un servidor anterior (sin `activity`) sigue contando el total.
  const unreadCount = data?.activity ?? data?.count ?? 0
  const newsUnread = data?.news ?? 0
  const showBadge = unreadCount > 0
  const showNewsDot = !showBadge && newsUnread > 0
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          showBadge
            ? t('notifications.bellAriaUnread', { count: unreadCount })
            : showNewsDot
              ? t('notifications.bellAriaNews')
              : t('notifications.bellAria')
        }
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex size-10 items-center justify-center rounded-md transition-colors hover:bg-yellow/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue"
      >
        <Bell size={20} strokeWidth={2.5} aria-hidden="true" />
        {showBadge && (
          // key={badgeLabel}: al cambiar el contador, el badge re-monta y hace
          // un pop de escala (llama la atención sobre la novedad).
          <m.span
            key={badgeLabel}
            aria-hidden="true"
            variants={badgePopVariants}
            initial="hidden"
            animate="visible"
            className="chip absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red px-1 font-brutal text-[10px] font-bold text-white"
          >
            {badgeLabel}
          </m.span>
        )}
        {showNewsDot && (
          <span
            aria-hidden="true"
            className="absolute right-1.5 top-1.5 size-2.5 rounded-full border-2 border-background bg-blue"
          />
        )}
      </button>

      {open && <NotificationDropdown onClose={() => setOpen(false)} />}
    </div>
  )
}
